<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import {
  PreprocessingTaskStatus,
  SignatureFieldType,
  SigningFileType,
  SigningProvider,
  SigningRequestSigneeRole,
  type VaultFileRevisionInDb,
} from '@/api';
import { isEnumValue } from '@/utils/enumUtils';
import GaiaAckList, { type GaiaAckItem } from '../composite/GaiaAckList.vue';
import GaiaBanner from '../composite/GaiaBanner.vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaDivider from '../base/GaiaDivider.vue';
import GaiaFieldRow from '../base/GaiaFieldRow.vue';
import GaiaLink from '../base/GaiaLink.vue';
import GaiaSectionHeader from '../base/GaiaSectionHeader.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaTextarea from '../base/inputs/GaiaTextarea.vue';
import GaiaParticipantRow from './GaiaParticipantRow.vue';
import GaiaAvatar from '../base/GaiaAvatar.vue';
import type { GaiaChipTone } from '../base/GaiaStatusChip.vue';
import { gaiaAccent, gaiaNeutral, gaiaStatus } from '../themes/tokens';
import { formatExpiryDay } from '@/composables/useSigningWizard';
import {
  signatureFieldTypeMap,
  signatureTypeMap,
  signeeRoleMap,
  signingProviderMap,
} from '../statusMaps';
import type {
  SignatureActivateRequest,
  SignatureFlowStepKey,
  SignatureReviewParty,
  SignatureSupersededDocument,
} from '@/types/contractManagement/signatureFlow';

/**
 * Step 5 of the prepare-signature wizard: the last look before the request
 * goes out. The summary reads off the payload that is actually about to be
 * sent, so what is shown and what is submitted cannot drift.
 *
 * The acknowledgements are a hard gate, not a nicety: activating replaces a
 * document that already carries a cryptographic signature, and that signature
 * cannot be recovered afterwards.
 */

const props = defineProps({
  requestPreview: {
    type: Object as PropType<SignatureActivateRequest>,
    required: true,
  },
  /**
   * The recipients by party uid — the participants payload carries references
   * only, so names come from here.
   */
  parties: {
    type: Array as PropType<SignatureReviewParty[]>,
    default: () => [],
  },
  /** Revisions whose existing signature will be cleared, one tick each. */
  revisionsRequiringAck: {
    type: Array as PropType<VaultFileRevisionInDb[]>,
    required: true,
  },
  acknowledgements: {
    type: Object as PropType<Record<string, boolean>>,
    required: true,
  },
  /** False while signature detection is still running on a document. */
  detectionComplete: {
    type: Boolean,
    default: true,
  },
  /** Documents whose file gained a newer revision after fields were placed. */
  supersededDocuments: {
    type: Array as PropType<SignatureSupersededDocument[]>,
    default: () => [],
  },
  /**
   * The message as typed. The preview's copy is trimmed for sending, so an
   * editor bound to it would swallow every trailing space mid-word.
   */
  message: {
    type: String,
    default: '',
  },
  /** Set when the last activation attempt failed. */
  error: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{
  'update:acknowledgements': [value: Record<string, boolean>];
  'go-to-step': [step: SignatureFlowStepKey];
  /** The signing email's message, written here instead of back on step 1. */
  'update:message': [value: string];
  /** The user chose to re-pin a document to its newer revision. */
  'adopt-document-revision': [vaultFileUid: string];
}>();

function supersededCopy(document: SignatureSupersededDocument): string {
  return (
    `${document.pinned_revision.file_name} was changed while you were preparing this signature ` +
    `request: the file is now at version ${document.new_revision.version_number}, you prepared ` +
    `version ${document.pinned_revision.version_number}. The signature fields you placed belong ` +
    `to the version you prepared and cannot be moved over automatically, because the pages may ` +
    `differ. Continue with the new version to place the fields again.`
  );
}

const documents = computed(() => props.requestPreview.documents);
const participants = computed(() => props.requestPreview.participants.participants);
const fields = computed(() => props.requestPreview.fields);

type Participant = (typeof participants.value)[number];

function participantName(participant: Participant): string {
  const party = props.parties.find(entry => entry.uid === participant.contractual_party_uid);
  return party?.name.trim() ? party.name : 'Unnamed recipient';
}

const signableDocuments = computed(() =>
  documents.value.filter(
    document => document.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT
  )
);
const attachments = computed(() => documents.value.length - signableDocuments.value.length);

function plural(word: string, count: number): string {
  return count === 1 ? word : `${word}s`;
}

const documentsLabel = computed(() =>
  [
    `${signableDocuments.value.length} signable ${plural('document', signableDocuments.value.length)}`,
    attachments.value > 0 ? `${attachments.value} ${plural('attachment', attachments.value)}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
);

const signatureTypes = computed(() => [
  ...new Set(
    participants.value
      .filter(participant => participant.role === SigningRequestSigneeRole.SIGNER)
      .map(participant => signatureTypeMap[participant.signature_type])
  ),
]);

const revisionsAwaitingDetection = computed(() =>
  signableDocuments.value
    .map(document => document.vault_file_revision)
    .filter(revision => {
      const status = revision.signature_detection_status;
      return (
        status == null ||
        status === PreprocessingTaskStatus.PENDING ||
        status === PreprocessingTaskStatus.RUNNING
      );
    })
);

function providerLabel(provider: string | null | undefined): string {
  if (!provider) return '—';

  return isEnumValue(SigningProvider, provider) ? signingProviderMap[provider] : provider;
}

function countInRole(role: SigningRequestSigneeRole): number {
  return participants.value.filter(participant => participant.role === role).length;
}

/** Who acts when, in words: approvers (if any) go first, then the signers. */
const orderLines = computed(() => {
  const lines: string[] = [];
  const approverCount = countInRole(SigningRequestSigneeRole.APPROVER);
  if (approverCount > 0) {
    lines.push(
      approverCount === 1
        ? 'The approver approves first'
        : props.requestPreview.initialize.ordered_approvers
          ? 'Approvers approve first, one after another'
          : 'Approvers approve first, all at the same time'
    );
  }
  const signerCount = countInRole(SigningRequestSigneeRole.SIGNER);
  const who = signerCount === 1 ? 'the signer signs' : 'signers sign';
  const how =
    signerCount <= 1
      ? ''
      : props.requestPreview.initialize.ordered_signers
        ? ', one after another'
        : ', all at the same time';
  const line = `${who}${how}`;
  lines.push(approverCount > 0 ? `Then ${line}` : line.charAt(0).toUpperCase() + line.slice(1));
  return lines;
});

// ── Recipients: who they are, and what each fills in ────────────────────────

const ROLE_ORDER: SigningRequestSigneeRole[] = [
  SigningRequestSigneeRole.APPROVER,
  SigningRequestSigneeRole.SIGNER,
  SigningRequestSigneeRole.FOLLOWER,
];

interface RecipientSummary {
  key: string;
  name: string;
  initials: string;
  /** Picks the avatar's and the chip's look. */
  roleKey: SigningRequestSigneeRole;
  role: string;
  detail: string;
  /** A signer with no signature field placed — they would have nothing to sign. */
  missingSignature: boolean;
  /** Their turn (1, 2, …) when their group acts one after another. */
  step?: number;
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');
}

/** "1 signature · 1 name" — each field type the recipient fills, counted. */
function fieldSummary(partyUid: string): { text: string; hasSignature: boolean } {
  const counts = new Map<SignatureFieldType, number>();
  for (const field of fields.value) {
    if (field.recipient_id !== partyUid) continue;
    counts.set(field.type, (counts.get(field.type) ?? 0) + 1);
  }
  const text = [...counts.entries()]
    .map(([type, count]) => `${count} ${signatureFieldTypeMap[type].toLowerCase()}`)
    .join(' · ');
  return { text, hasSignature: counts.has(SignatureFieldType.SIGNATURE) };
}

/** Whether a group acts one after another — only meaningful with two or more in it. */
function isOrdered(role: SigningRequestSigneeRole): boolean {
  if (countInRole(role) < 2) return false;
  if (role === SigningRequestSigneeRole.SIGNER) {
    return props.requestPreview.initialize.ordered_signers ?? false;
  }
  if (role === SigningRequestSigneeRole.APPROVER) {
    return props.requestPreview.initialize.ordered_approvers ?? false;
  }
  return false;
}

/** "Signs first" / "Signs after Alex Miller" — the participant array is the order. */
function turnText(verb: string, previous: string | undefined): string {
  return previous ? `${verb} after ${previous}` : `${verb} first`;
}

const recipients = computed<RecipientSummary[]>(() =>
  ROLE_ORDER.flatMap(role => {
    const inRole = participants.value.filter(participant => participant.role === role);
    const ordered = isOrdered(role);
    return inRole.map((participant, index): RecipientSummary => {
      const name = participantName(participant);
      const previous = index > 0 ? participantName(inRole[index - 1]) : undefined;
      const base = {
        key: `${role}-${participant.contractual_party_uid}-${index}`,
        name,
        initials: initialsOf(name),
        roleKey: role,
        role: signeeRoleMap[role],
        missingSignature: false,
        step: ordered ? index + 1 : undefined,
      };
      if (role === SigningRequestSigneeRole.APPROVER) {
        return {
          ...base,
          detail: ordered
            ? `${turnText('Approves', previous)}, before signing starts`
            : 'Approves the document before it goes out for signing',
        };
      }
      if (role === SigningRequestSigneeRole.FOLLOWER) {
        return { ...base, detail: 'Kept up to date and sent the signed copy' };
      }
      const { text, hasSignature } = fieldSummary(participant.contractual_party_uid);
      return {
        ...base,
        detail: [
          ordered ? turnText('Signs', previous) : '',
          hasSignature ? '' : 'No signature field placed',
          text,
        ]
          .filter(Boolean)
          .join(' · '),
        missingSignature: !hasSignature,
      };
    });
  })
);

/**
 * Each role reads apart at a glance: approvers green (they clear the way),
 * signers violet (they sign), followers an empty outline (they only watch).
 */
const ROLE_LOOK: Record<
  SigningRequestSigneeRole,
  { tone: GaiaChipTone; avatar: Record<string, string>; fill: string }
> = {
  [SigningRequestSigneeRole.APPROVER]: {
    tone: 'success',
    fill: gaiaStatus.successTint,
    avatar: { color: gaiaStatus.success },
  },
  [SigningRequestSigneeRole.SIGNER]: {
    tone: 'accent',
    fill: gaiaAccent.halo,
    avatar: { color: gaiaAccent.base },
  },
  [SigningRequestSigneeRole.FOLLOWER]: {
    tone: 'neutral',
    fill: 'transparent',
    avatar: {
      color: gaiaNeutral.textSubtle,
      border: `1px solid ${gaiaNeutral.borderCard}`,
    },
  },
};

const missingSignatureCount = computed(
  () => recipients.value.filter(recipient => recipient.missingSignature).length
);

// ── Message: written in place, it rides along with the send ─────────────────

const message = computed(() => props.requestPreview.initialize.message?.trim() ?? '');
const editingMessage = ref(false);

// ── Acknowledgements ────────────────────────────────────────────────────────

/**
 * Detection tells us *whether* a signature is there; a failed detection is
 * treated as "might be", so it needs the same tick.
 */
function ackText(revision: VaultFileRevisionInDb): string {
  if (revision.signature_detection_status === PreprocessingTaskStatus.FAILED) {
    return (
      `We could not analyse the existing signatures on ${revision.file_name}. Sending may ` +
      `invalidate cryptographic signatures that are present — visible signature images, filled ` +
      `fields and dates stay on the page, only the cryptographic proof is removed. The verifiable ` +
      `version remains as the file's previous revision.`
    );
  }

  return (
    `${revision.file_name} carries a digital signature. Sending invalidates it — visible ` +
    `signature images, filled fields and dates stay on the page, only the cryptographic proof is ` +
    `removed. The verifiable version remains as the file's previous revision.`
  );
}

const ackItems = computed<GaiaAckItem[]>(() =>
  props.revisionsRequiringAck.map(revision => ({
    key: revision.uid,
    checked: props.acknowledgements[revision.uid],
    text: ackText(revision),
  }))
);

function toggleAck(key: string, checked: boolean): void {
  const next = { ...props.acknowledgements };
  if (checked) {
    next[key] = true;
  } else {
    delete next[key];
  }
  emit('update:acknowledgements', next);
}
</script>

<template>
  <n-flex vertical :size="20" :wrap="false">
    <gaia-banner v-if="props.error" type="error">{{ props.error }}</gaia-banner>

    <gaia-banner
      v-for="document in props.supersededDocuments"
      :key="document.vault_file_uid"
      type="warning"
      title="A document changed while you were preparing this request"
    >
      {{ supersededCopy(document) }}
      <template #actions>
        <gaia-button
          size="small"
          secondary
          @click="emit('adopt-document-revision', document.vault_file_uid)"
        >
          Use version {{ document.new_revision.version_number }}
        </gaia-button>
      </template>
    </gaia-banner>

    <gaia-banner v-if="revisionsAwaitingDetection.length > 0" type="info">
      Checking
      {{ revisionsAwaitingDetection.length === 1 ? 'a document' : 'the documents' }}
      for existing signatures. Activation unlocks once that finishes.
    </gaia-banner>

    <gaia-ack-list
      v-if="ackItems.length > 0"
      type="warning"
      title="Existing signatures will be cleared"
      :items="ackItems"
      @toggle="toggleAck"
    >
      Each of these documents already carries a cryptographic signature. Confirm one by one before
      the request can be activated.
    </gaia-ack-list>

    <!-- The summary: a header rule, then the read-out as a two-column grid of
         stacked label/value cells — no card around it, as in the mock. -->
    <n-flex vertical :size="16" :wrap="false" data-tour="signing.activate-overview">
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-section-header>Summary</gaia-section-header>
        <gaia-divider variant="subtle" :style="{ flex: 1, margin: 0 }" />
      </n-flex>

      <n-grid cols="1 460:2" :x-gap="28" :y-gap="16">
        <n-gi>
          <gaia-field-row variant="stacked" label="Request name">
            <gaia-text
              weight="semibold"
              :tone="props.requestPreview.initialize.name ? 'ink' : 'muted'"
            >
              {{ props.requestPreview.initialize.name || '—' }}
            </gaia-text>
          </gaia-field-row>
        </n-gi>
        <n-gi>
          <gaia-field-row variant="stacked" label="Provider">
            {{ providerLabel(props.requestPreview.initialize.provider) }}
          </gaia-field-row>
        </n-gi>
        <n-gi>
          <gaia-field-row variant="stacked" label="Documents">{{ documentsLabel }}</gaia-field-row>
        </n-gi>
        <n-gi>
          <gaia-field-row variant="stacked" label="Expires">
            {{ formatExpiryDay(props.requestPreview.initialize.expiration_date) }}
          </gaia-field-row>
        </n-gi>
        <n-gi>
          <gaia-field-row variant="stacked" label="Signature level">
            {{ signatureTypes.length > 0 ? signatureTypes.join(', ') : '—' }}
          </gaia-field-row>
        </n-gi>
        <n-gi>
          <gaia-field-row variant="stacked" label="Order">
            <n-flex vertical :size="2">
              <span v-for="line in orderLines" :key="line">{{ line }}</span>
            </n-flex>
          </gaia-field-row>
        </n-gi>
      </n-grid>
    </n-flex>

    <!-- Each recipient once, approvers first: their role, and what they fill in. -->
    <n-flex vertical :size="10" :wrap="false">
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-section-header>Recipients</gaia-section-header>
        <gaia-divider variant="subtle" :style="{ flex: 1, margin: 0 }" />
      </n-flex>
      <n-card size="small" :content-style="{ padding: 0 }">
        <gaia-participant-row
          v-for="recipient in recipients"
          :key="recipient.key"
          :name="recipient.name"
          :initials="recipient.initials"
          :detail="recipient.detail"
          :state-label="recipient.role"
          :tone="recipient.missingSignature ? 'warning' : ROLE_LOOK[recipient.roleKey].tone"
        >
          <!-- Tinted by role; in an ordered group it carries their turn instead of initials. -->
          <template #avatar>
            <gaia-avatar
              :size="30"
              :color="ROLE_LOOK[recipient.roleKey].fill"
              :style="ROLE_LOOK[recipient.roleKey].avatar"
            >
              {{ recipient.step ?? recipient.initials }}
            </gaia-avatar>
          </template>
        </gaia-participant-row>
      </n-card>
      <n-flex v-if="missingSignatureCount > 0" align="center" :size="8">
        <gaia-text size="dense" tone="tertiary">
          {{ missingSignatureCount }} {{ plural('signer', missingSignatureCount) }} without a
          signature field.
        </gaia-text>
        <gaia-link @click="emit('go-to-step', 'review')">Place fields</gaia-link>
      </n-flex>
    </n-flex>

    <!-- The signing email's message, written right here. -->
    <n-flex vertical :size="10" :wrap="false">
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-section-header>Message</gaia-section-header>
        <gaia-divider variant="subtle" :style="{ flex: 1, margin: 0 }" />
      </n-flex>
      <n-flex v-if="editingMessage" vertical :size="8">
        <gaia-textarea
          :value="props.message"
          :autosize="{ minRows: 2, maxRows: 8 }"
          placeholder="Please countersign before month end."
          @update:value="(value: string) => emit('update:message', value)"
        />
        <n-flex justify="end">
          <gaia-button size="small" @click="editingMessage = false">Done</gaia-button>
        </n-flex>
      </n-flex>
      <n-flex v-else-if="message" align="flex-start" :size="12" :wrap="false">
        <gaia-text size="body" tone="ink" :style="{ whiteSpace: 'pre-wrap', flex: 1 }">
          {{ message }}
        </gaia-text>
        <gaia-link @click="editingMessage = true">Edit</gaia-link>
      </n-flex>
      <n-flex v-else align="center" :size="12">
        <gaia-text size="dense" tone="tertiary">
          No message — recipients get the default signing email.
        </gaia-text>
        <gaia-link @click="editingMessage = true">Add a message</gaia-link>
      </n-flex>
    </n-flex>
  </n-flex>
</template>
